/**
 * Premier Auto Designs - Wishlist Engine
 * Handles saving, toggling, persistent storage, live counter badge, and Wishlist page rendering.
 */
(function() {
  'use strict';

  const STORAGE_KEY = 'pad_wishlist_items';

  const Wishlist = {
    get: function() {
      try {
        const data = localStorage.getItem(STORAGE_KEY);
        return data ? JSON.parse(data) : [];
      } catch (e) {
        console.error('Error reading wishlist from localStorage:', e);
        return [];
      }
    },

    save: function(items) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        this.dispatchChange();
      } catch (e) {
        console.error('Error saving wishlist to localStorage:', e);
      }
    },

    has: function(handle, id) {
      const items = this.get();
      return items.some(item => {
        if (handle && item.handle && item.handle === handle) return true;
        if (id && item.id && String(item.id) === String(id)) return true;
        return false;
      });
    },

    add: function(item) {
      if (!item || (!item.handle && !item.id)) return;
      const items = this.get();
      if (!this.has(item.handle, item.id)) {
        items.unshift({
          id: item.id || '',
          handle: item.handle || '',
          title: item.title || 'Product',
          vendor: item.vendor || 'Premier Auto Designs',
          price: item.price || '$0.00',
          price_raw: item.price_raw || 0,
          compare_at_price: item.compare_at_price || '',
          image: item.image || '',
          url: item.url || '#',
          variant_id: item.variant_id || '',
          available: item.available !== false && item.available !== 'false',
          added_at: Date.now()
        });
        this.save(items);
        this.showToast('Added to Wishlist', item.title, '/pages/wishlist');
      }
    },

    remove: function(handle, id) {
      let items = this.get();
      const removedItem = items.find(item => 
        (handle && item.handle === handle) || (id && String(item.id) === String(id))
      );
      items = items.filter(item => {
        if (handle && item.handle === handle) return false;
        if (id && String(item.id) === String(id)) return false;
        return true;
      });
      this.save(items);
      if (removedItem) {
        this.showToast('Removed from Wishlist', removedItem.title);
      }
    },

    toggle: function(item) {
      if (this.has(item.handle, item.id)) {
        this.remove(item.handle, item.id);
        return false; // Removed
      } else {
        this.add(item);
        return true; // Added
      }
    },

    clear: function() {
      this.save([]);
      this.showToast('Wishlist Cleared', 'All saved items have been removed.');
    },

    count: function() {
      return this.get().length;
    },

    dispatchChange: function() {
      const event = new CustomEvent('pad:wishlist:change', {
        detail: {
          items: this.get(),
          count: this.count()
        }
      });
      document.dispatchEvent(event);
    },

    updateBadges: function() {
      const total = this.count();
      const badges = document.querySelectorAll('[data-wishlist-count]');
      badges.forEach(badge => {
        badge.textContent = total;
        if (total > 0) {
          badge.style.display = 'flex';
          badge.classList.remove('badge-pop');
          void badge.offsetWidth; // Trigger reflow for animation
          badge.classList.add('badge-pop');
        } else {
          badge.style.display = 'none';
        }
      });
    },

    updateButtons: function() {
      const buttons = document.querySelectorAll('[data-wishlist-btn]');
      buttons.forEach(btn => {
        const handle = btn.getAttribute('data-product-handle');
        const id = btn.getAttribute('data-product-id');
        const isIn = this.has(handle, id);
        
        if (isIn) {
          btn.classList.add('is-active');
          btn.setAttribute('aria-pressed', 'true');
          const title = btn.getAttribute('data-product-title') || 'Product';
          btn.setAttribute('aria-label', `Remove ${title} from Wishlist`);
        } else {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-pressed', 'false');
          const title = btn.getAttribute('data-product-title') || 'Product';
          btn.setAttribute('aria-label', `Add ${title} to Wishlist`);
        }
      });
    },

    showToast: function(heading, message, linkUrl) {
      let toastContainer = document.getElementById('pad-toast-container');
      if (!toastContainer) {
        toastContainer = document.createElement('div');
        toastContainer.id = 'pad-toast-container';
        toastContainer.className = 'pad-toast-container';
        document.body.appendChild(toastContainer);
      }

      const toast = document.createElement('div');
      toast.className = 'pad-toast';
      
      const iconHtml = `<div class="pad-toast-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="#ea0202" stroke="#ea0202" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg></div>`;
      
      let bodyHtml = `
        <div class="pad-toast-content">
          <div class="pad-toast-title">${heading}</div>
          ${message ? `<div class="pad-toast-msg">${message}</div>` : ''}
        </div>
      `;

      if (linkUrl) {
        bodyHtml += `<a href="${linkUrl}" class="pad-toast-link">View</a>`;
      }

      const closeBtnHtml = `<button type="button" class="pad-toast-close" aria-label="Close notification"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg></button>`;

      toast.innerHTML = iconHtml + bodyHtml + closeBtnHtml;
      toastContainer.appendChild(toast);

      // Animate in
      requestAnimationFrame(() => {
        toast.classList.add('pad-toast-show');
      });

      const removeToast = () => {
        toast.classList.remove('pad-toast-show');
        toast.classList.add('pad-toast-hide');
        setTimeout(() => {
          if (toast.parentNode) toast.parentNode.removeChild(toast);
        }, 300);
      };

      const closeBtn = toast.querySelector('.pad-toast-close');
      if (closeBtn) closeBtn.addEventListener('click', removeToast);

      setTimeout(removeToast, 3800);
    },

    renderWishlistPage: function() {
      const container = document.getElementById('wishlist-grid-container');
      const countEl = document.getElementById('wishlist-total-count');
      const emptyState = document.getElementById('wishlist-empty-state');
      const contentState = document.getElementById('wishlist-content-state');
      
      if (!container) return; // Not on wishlist page

      const items = this.get();
      if (countEl) {
        countEl.textContent = `${items.length} ${items.length === 1 ? 'item' : 'items'}`;
      }

      if (items.length === 0) {
        if (emptyState) emptyState.style.display = 'block';
        if (contentState) contentState.style.display = 'none';
        container.innerHTML = '';
        return;
      }

      if (emptyState) emptyState.style.display = 'none';
      if (contentState) contentState.style.display = 'block';

      container.innerHTML = items.map(item => {
        const hasCompare = item.compare_at_price && item.compare_at_price !== item.price;
        const imgUrl = item.image || '/logo-pad.avif';
        return `
          <article class="product-card pad-wishlist-card" data-handle="${item.handle}" data-id="${item.id}">
            <div class="card-badges">
              <span class="badge badge-featured">Saved</span>
            </div>
            
            <button type="button" 
              class="card-wishlist-btn is-active" 
              aria-label="Remove ${item.title} from Wishlist"
              data-wishlist-btn
              data-product-handle="${item.handle}"
              data-product-id="${item.id}"
              data-product-title="${item.title.replace(/"/g, '&quot;')}"
              data-product-vendor="${item.vendor.replace(/"/g, '&quot;')}"
              data-product-price="${item.price}"
              data-product-compare="${item.compare_at_price}"
              data-product-image="${item.image}"
              data-product-url="${item.url}"
              data-product-available="${item.available}">
              <svg class="heart-icon" width="18" height="18" viewBox="0 0 24 24" fill="#ea0202" stroke="#ea0202" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
              </svg>
            </button>
            
            <a href="${item.url}" class="card-image-area" style="display: block;">
              <div class="card-image-hover-gradient"></div>
              <img src="${imgUrl}" alt="${item.title.replace(/"/g, '&quot;')}" class="card-image" loading="lazy" />
            </a>
            
            <div class="card-body">
              <div class="card-brand-rating">
                <span class="card-brand">${item.vendor}</span>
                <div class="card-rating">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="#ea0202" stroke="#ea0202" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  <span class="score">5.0</span>
                  <span>(12)</span>
                </div>
              </div>
              
              <h3 class="card-title">
                <a href="${item.url}" style="text-decoration: none; color: inherit;">${item.title}</a>
              </h3>
              
              <div class="card-footer">
                <div class="card-price-col">
                  ${hasCompare ? `<span class="card-price-old">${item.compare_at_price}</span>` : ''}
                  <span class="card-price card-price-white">${item.price}</span>
                </div>
                
                <div style="display:flex; gap: 0.5rem; align-items: center;">
                  <button type="button" class="btn-add btn-options" data-product-handle="${item.handle}">
                    Options
                  </button>
                </div>
              </div>
            </div>
          </article>
        `;
      }).join('');

      // Initialize tilt effect on new cards if available
      if (typeof window.initTiltEffect === 'function') {
        window.initTiltEffect();
      }
    },

    init: function() {
      // Listen to wishlist changes to update UI
      document.addEventListener('pad:wishlist:change', () => {
        this.updateBadges();
        this.updateButtons();
        this.renderWishlistPage();
      });

      // Listen to multi-tab storage changes
      window.addEventListener('storage', (e) => {
        if (e.key === STORAGE_KEY) {
          this.updateBadges();
          this.updateButtons();
          this.renderWishlistPage();
        }
      });

      // Global click handler for wishlist toggle buttons
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-wishlist-btn]');
        if (btn) {
          e.preventDefault();
          e.stopPropagation();

          const itemData = {
            id: btn.getAttribute('data-product-id') || '',
            handle: btn.getAttribute('data-product-handle') || '',
            title: btn.getAttribute('data-product-title') || '',
            vendor: btn.getAttribute('data-product-vendor') || 'Premier Auto Designs',
            price: btn.getAttribute('data-product-price') || '$0.00',
            price_raw: btn.getAttribute('data-product-price-raw') || 0,
            compare_at_price: btn.getAttribute('data-product-compare') || '',
            image: btn.getAttribute('data-product-image') || '',
            url: btn.getAttribute('data-product-url') || '#',
            variant_id: btn.getAttribute('data-product-variant-id') || '',
            available: btn.getAttribute('data-product-available') !== 'false'
          };

          this.toggle(itemData);
        }

        const clearBtn = e.target.closest('#clear-wishlist-btn');
        if (clearBtn) {
          e.preventDefault();
          if (confirm('Are you sure you want to remove all saved items from your wishlist?')) {
            this.clear();
          }
        }
      });

      // Initial UI update
      this.updateBadges();
      this.updateButtons();
      this.renderWishlistPage();
    }
  };

  // Expose globally
  window.PAD_Wishlist = Wishlist;

  // Initialize once DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => Wishlist.init());
  } else {
    Wishlist.init();
  }
})();
